import { EditEdge, EditRow, EditTable } from "diagram/model/edit/edit";
import { GRID_CELL } from "geometry/geometry";

/**
 * This module prevents lines from overlapping horizontally. This usually
 * happens when multiple Edges point to the same row:
 * BAD:  A ----+----> C
 *             |
 *       B ----+
 *
 * GOOD: A ---------> C1 (C row is bigger)
 *             +----> C2
 *       B ----+
 */

type TID = EditTable["id"];
type RID = EditRow["id"];

interface State {
    countMap: Map<TID, Map<RID, number>>;
    totalMap: Map<TID, Map<RID, number>>;
}

export type EdgeSplitYState = State;

const STEP = GRID_CELL;

/**
 * Returns the distance that an Edge should move where it points to the row.
 * Keep multiple incoming arrows evenly centered inside the expanded row.
 */
const getOffset = (state: State, tableId: TID, rowId: RID): number => {
    const index = state.countMap.get(tableId)?.get(rowId) ?? 0;
    const total = state.totalMap.get(tableId)?.get(rowId) ?? 1;
    return (index - (total - 1) / 2) * STEP;
};

const save = (state: State, tableId: TID, rowId: RID): void => {
    const table: Map<RID, number> = state.countMap.get(tableId) ?? new Map();
    table.set(rowId, (table.get(rowId) ?? 0) + 1);
    state.countMap.set(tableId, table);
};

const setCount = (map: State["totalMap"], tableId: TID, rowId: RID): void => {
    const table: Map<RID, number> = map.get(tableId) ?? new Map();
    table.set(rowId, (table.get(rowId) ?? 0) + 1);
    map.set(tableId, table);
};

const init = (edges: EditEdge[]): State => {
    const totalMap: State["totalMap"] = new Map();
    edges.forEach((edge) => {
        edge.to.rows.forEach((rowId) =>
            setCount(totalMap, edge.to.table, rowId)
        );
    });
    return { countMap: new Map(), totalMap };
};

export const EdgeSplitY = {
    init,
    getOffset,
    save,
};
